import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from 'react-native';

import { Button, ConfirmDialog, EmptyState, ProgressStepper, SkeletonRow } from '~/components/core';
import CustomHeader from '~/components/navigators/CustomHeader';
import { StrategyInputNode, StrategyTemplate } from '~/api-services/AIStrategyBuilderService/types';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { useCreateStrategyWizard, WizardStep } from './hooks/useCreateStrategyWizard';
import RecapBar from './components/wizard/RecapBar';
import StepMarket from './components/wizard/steps/StepMarket';
import StepOptimisation from './components/wizard/steps/StepOptimisation';
import StepReview from './components/wizard/steps/StepReview';
import StepRules from './components/wizard/steps/StepRules';
import StepTradeSetup from './components/wizard/steps/StepTradeSetup';

type Props = {
    onCreated: (strategyId?: string) => void;
    // The wizard's own back control. The host decides what leaving means.
    onClose: () => void;
    template?: StrategyTemplate;
    cloneNodes?: StrategyInputNode[];
};

/**
 * Lets a host ask to close the wizard without knowing whether there is unsaved input: a clean
 * draft calls `proceed` straight away, a dirty one opens the discard dialog and calls it only if
 * the user confirms. The host owns the leave itself (a stack `beforeRemove` on mobile, a pane
 * switch on desktop); the wizard owns the question.
 */
export type CreateStrategyWizardHandle = { requestExit: (proceed: () => void) => void };

const STEP_TITLE_KEYS: Record<WizardStep, string> = {
    market: 'ai-strategy-step-market',
    trade: 'ai-strategy-step-trade',
    rules: 'ai-strategy-step-rules',
    optimisation: 'ai-strategy-step-optimisation',
    review: 'ai-strategy-step-review',
};

/**
 * The create wizard (docs/ai-strategy/03-create-strategy-wizard.md): one route with internal
 * steps, a progress header, and a tappable recap bar so earlier choices stay visible and
 * editable. Forward navigation is gated per-step; leaving with a dirty draft asks first.
 * The step list is account-driven (`wizard.steps`) — accounts without optimisations get four
 * steps instead of five — so nothing here may assume a fixed step index.
 */
function CreateStrategyWizard(
    { onCreated, onClose, template, cloneNodes }: Props,
    ref: React.Ref<CreateStrategyWizardHandle>
) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const wizard = useCreateStrategyWizard({ template, cloneNodes, onCreated });
    const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);

    // The app-standard header (CustomHeader, as in Settings) carries the current step title
    // ("Market", "Trade setup"…) — the stepper below shows only the progress segments (no
    // redundant "Step 1/5 Market" line).
    const stepTitle = i18n.t(STEP_TITLE_KEYS[wizard.steps[wizard.step]]);

    // isDirty() reads a ref: a successful create clears it immediately, so the jump to the new
    // strategy's detail is never intercepted.
    const requestExit = (proceed: () => void) => {
        if (!wizard.isDirty()) {
            proceed();
            return;
        }
        setPendingExit(() => proceed);
    };
    useImperativeHandle(ref, () => ({ requestExit }));

    // Back steps through the wizard like the footer button; only from the first step does it
    // mean "leave", which is where the discard question belongs.
    const header = (
        <CustomHeader
            title={stepTitle}
            noTranslateTitle
            onBack={() => (wizard.step > 0 ? wizard.setStep(wizard.step - 1) : requestExit(onClose))}
        />
    );

    if (wizard.metaLoading) {
        return (
            <>
                {header}
                <View style={styles.loading}>
                    <SkeletonRow height={14} width="40%" />
                    <SkeletonRow height={44} />
                    <SkeletonRow height={44} />
                    <SkeletonRow height={44} />
                </View>
            </>
        );
    }

    if (wizard.metaError) {
        return (
            <>
                {header}
                <EmptyState title="ai-strategy-wizard-meta-failed" ctaLabel="retry" onCtaPress={wizard.retryMeta} />
            </>
        );
    }

    const step = wizard.step;
    const current = wizard.steps[step];
    const isReview = current === 'review';
    const recapText: Record<WizardStep, string> = {
        market: wizard.summaries.market,
        trade: wizard.summaries.trade,
        rules: wizard.summaries.rules,
        optimisation: wizard.summaries.optimisationText,
        // Review recaps everything itself, so it contributes no chip.
        review: '',
    };
    // One chip per rendered step — RecapBar drops the empty and not-yet-reached ones.
    const recapEntries = wizard.steps.map((name, index) => ({ step: index, text: recapText[name] }));

    return (
        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            {header}
            <View style={styles.column}>
                <View style={styles.stepperWrap}>
                    <ProgressStepper steps={wizard.steps.length} current={step} />
                </View>

                {/* Review already lists every prior choice, so the recap chips are hidden there. */}
                {!isReview ? <RecapBar entries={recapEntries} currentStep={step} onJump={wizard.setStep} /> : null}

                <ScrollView
                    style={styles.flex}
                    contentContainerStyle={styles.stepContent}
                    keyboardShouldPersistTaps="handled"
                >
                    {current === 'market' ? <StepMarket wizard={wizard} /> : null}
                    {current === 'trade' ? <StepTradeSetup wizard={wizard} /> : null}
                    {current === 'rules' ? <StepRules wizard={wizard} /> : null}
                    {current === 'optimisation' ? <StepOptimisation wizard={wizard} /> : null}
                    {current === 'review' ? <StepReview wizard={wizard} /> : null}
                </ScrollView>

                <View style={styles.footer}>
                    {step > 0 ? (
                        <View style={styles.footerButton}>
                            <Button
                                title="ai-strategy-back"
                                onPress={() => wizard.setStep(step - 1)}
                                backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                                titleColor={theme.text}
                            />
                        </View>
                    ) : null}
                    <View style={styles.footerButton}>
                        {isReview ? (
                            <Button
                                title={wizard.submitting ? 'ai-strategy-creating' : 'ai-strategy-create'}
                                onPress={wizard.submit}
                                disabled={wizard.submitting || wizard.cooldownSeconds > 0 || !wizard.stepValid[step]}
                            />
                        ) : (
                            <Button
                                title="ai-strategy-next"
                                onPress={() => {
                                    wizard.markDirty();
                                    wizard.setStep(step + 1);
                                }}
                                disabled={!wizard.stepValid[step]}
                            />
                        )}
                    </View>
                </View>
            </View>

            {pendingExit ? (
                <ConfirmDialog
                    visible
                    title="ai-strategy-discard-draft-title"
                    message="ai-strategy-discard-draft-body"
                    destructive
                    confirmLabel="ai-strategy-discard-draft-confirm"
                    onConfirm={() => {
                        const proceed = pendingExit;
                        setPendingExit(null);
                        proceed();
                    }}
                    onCancel={() => setPendingExit(null)}
                />
            ) : null}
        </KeyboardAvoidingView>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        flex: { flex: 1 },
        // Centered max-width column so the wizard doesn't stretch across wide desktop panes.
        column: {
            flex: 1,
            width: '100%',
            maxWidth: scaleSize(680),
            alignSelf: 'center',
        },
        loading: {
            padding: scaleSize(16),
        },
        stepperWrap: {
            paddingHorizontal: scaleSize(12),
        },
        stepContent: {
            paddingHorizontal: scaleSize(12),
            paddingBottom: scaleSize(16),
        },
        footer: {
            flexDirection: 'row',
            padding: scaleSize(12),
            borderTopWidth: StyleSheet.hairlineWidth,
            borderTopColor: theme.tabIconDefault,
        },
        footerButton: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
    });

export default forwardRef(CreateStrategyWizard);
